import { Record as ImmutableRecord } from 'immutable'
import { ServerSession } from '../session'
import { EwsMessage, FindByEmailResponse, CrmReferences, Recipient, Guest } from '../../types/loft'
import { Me } from '../../types/me'
import { CalendarSettings } from '../../types/meetings'
import { CrmCustomFieldType } from '../../types/crmFields'
import { TeamEmailSettings } from '../../types/teamEmailSettings'
import { TeamPrivacySettings } from '../../types/user'

export type LoftAsyncProps = {
  to?: Promise<
    {
      emailAddress: string
    }[]
  >
  subject?: Promise<string>
  body?: Promise<string>
  attachments?: Promise<[]>
  templateId?: Promise<number>
  me?: Promise<Me>
  teamEmailSettings?: Promise<{
    outlook_disallow_rest: boolean
  }>
  publicTeamEmailSettings?: Promise<TeamEmailSettings>
  teamPrivacySettings?: Promise<TeamPrivacySettings>
  ewsMessage?: Promise<EwsMessage>
  recipient?: Promise<FindByEmailResponse>
  updateParentEmail?: Promise<void>
  sendEmail?: Promise<void>
  crmReferences?: Promise<CrmReferences>
  calendarSettings?: Promise<{ data: CalendarSettings }>
  meetingTypeId?: Promise<number | string>
  recurrence?: Promise<Record<string, unknown>>
  location?: Promise<string>
  attendees?: Promise<Recipient[]>
  organizer?: Promise<Guest | Recipient>
  startTime?: Promise<string>
  endTime?: Promise<string>
  mainGuest?: Promise<Recipient | Guest>
  crmCustomFields?: Promise<CrmCustomFieldType[]>
  itemType?: Promise<string>
  itemId?: Promise<string>
  eventData?: Promise<Record<string, unknown>>
}

type LoftProps = {
  async: LoftAsyncProps
  strategy: string
  syncId: string | null
  restItemId: string | null
  ewsItemId: string | null
  session: ServerSession | null
  emailAddress: string | null
  mailingId: number | null
  draftMailingId: number | null
  trackOpens: boolean
  trackClicks: boolean
  email: null | {
    tracking_body: string
    subject: string
  }
}

const defaultValues: LoftProps = {
  strategy: 'preloft',
  syncId: null,
  restItemId: null,
  ewsItemId: null,
  session: null,
  emailAddress: null,
  mailingId: null,
  email: null,
  draftMailingId: null,
  trackOpens: true,
  trackClicks: true,
  async: {
    to: undefined,
    subject: undefined,
    body: undefined,
    attachments: undefined,
    templateId: undefined,
    me: undefined,
    teamEmailSettings: undefined,
    ewsMessage: undefined,
    recipient: undefined,
    updateParentEmail: undefined,
    sendEmail: undefined,
    crmReferences: undefined,
  },
}

export class LoftRecord extends ImmutableRecord(defaultValues) {
  async getAsync<T extends keyof LoftAsyncProps>(name: T): Promise<LoftAsyncProps[T]> {
    const value = this.getIn(['async', name])
    if (value === undefined) {
      throw new Error(`Broken pipe: "${name}" promise is not set`)
    }
    return value as LoftAsyncProps[T]
  }

  setAsync<T extends keyof LoftAsyncProps, K>(name: T, fn: K): LoftRecord {
    return this.setIn(['async', name], fn)
  }

  async getEmailClientType(): Promise<string> {
    try {
      const user = await this.getAsync('me')
      return user?.data?._private_fields?.email_client_type.toLowerCase() || 'none'
    } catch (e) {
      return 'not_resolved'
    }
  }
}

export default new LoftRecord()
